/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";

/**
 * The first thing a signed-out visitor sees on the collected wallet — usually
 * arriving from a receipt QR with a reward to claim, before they have an
 * account. The bare "sign in" card gave them no reason to; this sells the
 * wallet and makes the account feel like the thing standing between them and a
 * reward that is already waiting.
 *
 * When the receipt advertises a specific membership, `program` carries its
 * public marketing fields (fetched anonymously) so the hero can name the actual
 * programme and place instead of the generic pitch.
 *
 * It renders behind the auth dialog, so the CTA and the dialog do the same
 * thing: both open `showAuthDialog`. The fanned cards are decorative only.
 */

const FanCard = defineComponent<{ tone: string; offset: string }>({
  name: "SignedOutHeroFanCard",
  props: ["tone", "offset"],
  setup(props) {
    return () => (
      <div
        aria-hidden="true"
        class={[
          "absolute inset-x-0 top-0 mx-auto aspect-[8/5] w-40 rounded-2xl text-primary shadow-lg ring-1 ring-primary/15",
          props.tone,
          props.offset,
        ]}
      >
        <div class="absolute left-3 top-3 size-6 rounded-full bg-current/25" />
        <div class="absolute bottom-3 left-3 right-8 h-1.5 rounded-full bg-current/25" />
        <div class="absolute bottom-6 left-3 right-14 h-1.5 rounded-full bg-current/15" />
      </div>
    );
  },
});

const Benefit = defineComponent<{ icon: string; title: string; copy: string }>({
  name: "SignedOutHeroBenefit",
  props: ["icon", "title", "copy"],
  setup(props) {
    return () => (
      <div class="flex items-start gap-3">
        <span class="grid size-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
          <span class="material-symbols-outlined text-xl" aria-hidden="true">
            {props.icon}
          </span>
        </span>
        <div class="min-w-0">
          <p class="font-semibold text-base-content">{props.title}</p>
          <p class="mt-0.5 text-sm text-base-content/60">{props.copy}</p>
        </div>
      </div>
    );
  },
});

export type SignedOutHeroProgram = {
  name: string;
  description: string | null;
  place_name: string;
};

export type SignedOutHeroProps = {
  /** A receipt is waiting to be claimed once the visitor signs in. */
  pendingReward?: boolean;
  /** The specific membership the receipt advertises, if known. */
  program?: SignedOutHeroProgram | null;
  onSignIn: () => void;
};

export const SignedOutHero = defineComponent<SignedOutHeroProps>({
  name: "SignedOutHero",
  props: ["pendingReward", "program", "onSignIn"],
  setup(props) {
    const { t } = useLocalization();

    return () => (
      <div class="mx-auto flex w-full flex-col p-2">
        <div class="overflow-hidden rounded-3xl border border-base-300 bg-base-100 shadow-sm ">
          {/* Hero band with the fanned cards. */}
          <div class="relative overflow-hidden bg-gradient-to-b from-primary/50 via-base-100 to-base-100 px-6 pb-8 pt-10 text-center sm:px-10 sm:pt-12">
            <div class="relative mx-auto mb-6 h-32 sm:h-40">
              <FanCard tone="bg-primary/20" offset="translate-y-3 -rotate-12" />
              <FanCard tone="bg-primary/40" offset="translate-y-1 rotate-12" />
              <FanCard
                tone="bg-primary text-primary-content"
                offset="-translate-y-1 rotate-0"
              />
            </div>

            {props.pendingReward && (
              <span class="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                <span
                  class="material-symbols-outlined text-sm"
                  aria-hidden="true"
                >
                  redeem
                </span>
                {t("customer.collected.welcome_reward_waiting")}
              </span>
            )}

            <h1 class="mt-3 text-2xl font-bold leading-tight text-base-content sm:text-3xl">
              {props.pendingReward
                ? t("customer.collected.welcome_title_reward")
                : t("customer.collected.welcome_title")}
            </h1>
            <p class="mx-auto mt-2 max-w-sm text-sm text-base-content/60 sm:text-base">
              {t("customer.collected.welcome_subtitle")}
            </p>
          </div>

          <div class="space-y-5 px-6 pb-6 sm:px-10 sm:pb-8">
            {/* The named membership the receipt is inviting them into. */}
            {props.program && (
              <div class="rounded-2xl border border-primary/20 bg-primary/5 p-4">
                <p class="text-[11px] font-semibold uppercase tracking-wider text-primary/80">
                  {t("customer.collected.welcome_program_label")}
                </p>
                <p class="mt-1 font-bold text-base-content">
                  {props.program.name}
                </p>
                <p class="flex items-center gap-1 text-xs text-base-content/60">
                  <span
                    class="material-symbols-outlined text-sm"
                    aria-hidden="true"
                  >
                    storefront
                  </span>
                  {props.program.place_name}
                </p>
                {props.program.description && (
                  <p class="mt-2 text-sm text-base-content/70">
                    {props.program.description}
                  </p>
                )}
              </div>
            )}

            {/* Benefits sit two-up on wider screens so the card never grows into
                one tall column on desktop. */}
            <div class="grid gap-4 sm:grid-cols-2">
              <Benefit
                icon="qr_code_scanner"
                title={t("customer.collected.welcome_benefit_collect_title")}
                copy={t("customer.collected.welcome_benefit_collect_copy")}
              />
              <Benefit
                icon="local_activity"
                title={t("customer.collected.welcome_benefit_rewards_title")}
                copy={t("customer.collected.welcome_benefit_rewards_copy")}
              />
              <Benefit
                icon="bolt"
                title={t("customer.collected.welcome_benefit_wallet_title")}
                copy={t("customer.collected.welcome_benefit_wallet_copy")}
              />
            </div>
          </div>
        </div>

        {/* The mobile CTA is fixed and therefore contributes no document
            height. This spacer gives the page enough trailing scroll range to
            move the final benefit fully above the CTA and bottom navigation.
            Desktop lays the CTA out normally, so it needs no clearance. */}
        <div aria-hidden="true" class="h-48 shrink-0 sm:hidden" />

        {/* On a phone the CTA is pinned above the fixed tab bar (67px tall) so
            it stays reachable no matter how short the content is; on desktop it
            settles inline under the card. */}
        <div
          class="fixed inset-x-0 px-4 sm:static sm:inset-x-auto sm:z-auto sm:mt-5 sm:px-0"
          style={{ bottom: "calc(5rem)" }}
        >
          <div class="mx-auto rounded-2xl">
            <button
              type="button"
              class="btn btn-primary btn-block btn-lg rounded-2xl"
              onClick={() => props.onSignIn()}
            >
              {props.pendingReward
                ? t("customer.collected.welcome_cta_reward")
                : t("customer.collected.welcome_cta")}
            </button>
            <p class="mt-2 text-center text-xs text-base-content/50">
              {t("customer.collected.welcome_reassure")}
            </p>
          </div>
        </div>
      </div>
    );
  },
});
